import { useMutation } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  CreateTerrainModelJobParameters,
  DeleteTerrainModelParameters,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isValidNumber } from '../../../../utils/math/number';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

function useDeleteMutation(props: { onError: (error: Error) => void; onSuccess: () => void }) {
  return useMutation({
    mutationFn: async (params: DeleteTerrainModelParameters) =>
      SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().deleteTerrainModel(params),
    retry: false,
    networkMode: 'always',
    onError(error) {
      props.onError(error);
    },
    onSuccess() {
      props.onSuccess();
    },
  });
}

function useSubmitMutation(props: { onError: (error: Error) => void; onSuccess: () => void }) {
  return useMutation({
    mutationFn: async (params: CreateTerrainModelJobParameters) =>
      SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().createTerrainModelJob(params),
    retry: false,
    networkMode: 'always',
    onError(error) {
      props.onError(error);
    },
    onSuccess() {
      props.onSuccess();
    },
  });
}

export const TerrainModelDialog = (props: {
  dialog: ReturnType<typeof useDialog>;
  geodata: GeodataModel;
}) => {
  const { buildErrorList, handleError } = useErrorHandling();
  const { t } = useTranslation('cloudProcessing');
  const [useHeightField, setUseHeightField] = useState(false);
  const [decimateFaceCount, setDecimateFaceCount] = useState('150000');

  const onSuccess = useMemo(() => {
    return () => {
      props.dialog.hide();
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: props.geodata.terrainModel
          ? t('geodataPage.terrainModelDialog.successRemoved', { ns: 'cloudProcessing' })
          : t('geodataPage.terrainModelDialog.successQueued', { ns: 'cloudProcessing' }),
      });
    };
  }, [props.dialog, props.geodata, t]);

  const onError = useMemo(() => {
    return (error: Error) => handleError(error);
  }, [handleError]);

  const deleteMutation = useDeleteMutation({ onSuccess, onError });
  const submitMutation = useSubmitMutation({ onSuccess, onError });

  const isPending = deleteMutation.isPending || submitMutation.isPending;
  const canCreateTerrainModelJob =
    props.geodata.status === 'generate_pointcloud_finished' ||
    props.geodata.status === 'generate_skyview_data_finished';

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} onClose={props.dialog.hide}>
      {{
        header: t('geodataPage.terrainModelDialog.header', { ns: 'cloudProcessing' }),
        content: (
          <OverlayLoader visible={isPending}>
            <Stack spacing={1}>
              <InfoBox color="yellow">
                {props.geodata.terrainModel
                  ? t('geodataPage.terrainModelDialog.descriptionExisting', {
                      ns: 'cloudProcessing',
                    })
                  : t('geodataPage.terrainModelDialog.descriptionNew', { ns: 'cloudProcessing' })}
              </InfoBox>
              {!props.geodata.terrainModel && (
                <LabelledContainer
                  text={t('geodataPage.terrainModelDialog.decimateLabel', {
                    ns: 'cloudProcessing',
                  })}
                >
                  <TextBox
                    step={1}
                    type="number"
                    value={decimateFaceCount}
                    onChange={(e) => setDecimateFaceCount(e.target.value)}
                  />
                </LabelledContainer>
              )}
              {!props.geodata.terrainModel && (
                <CheckBox
                  checked={useHeightField}
                  onChange={() => setUseHeightField((oldValue) => !oldValue)}
                >
                  Use height field
                </CheckBox>
              )}
              {buildErrorList()}
            </Stack>
          </OverlayLoader>
        ),
        footer: {
          left: (
            <>
              {props.geodata.terrainModel ? (
                <Button
                  color="error"
                  leftIcon={{ icon: ['fad', 'trash-alt'] }}
                  variant="contained"
                  onClick={() => deleteMutation.mutate({ path: { geodataId: props.geodata.id } })}
                >
                  {t('delete', { ns: 'common' })}
                </Button>
              ) : (
                <Button
                  color="primary"
                  disabled={!canCreateTerrainModelJob || !isValidNumber(decimateFaceCount)}
                  variant="contained"
                  onClick={() =>
                    submitMutation.mutate({
                      path: { geodataId: props.geodata.id },
                      body: { useHeightField, decimateFaceCount: parseInt(decimateFaceCount) },
                    })
                  }
                >
                  {t('start', { ns: 'common' })}
                </Button>
              )}
            </>
          ),
        },
      }}
    </Dialog>
  );
};
